import React, { useState, useEffect, useCallback } from 'react';
import { remindersApi } from '../services/api';
import Navbar from '../components/Navbar';
import ConfirmModal from '../components/ConfirmModal';
import { useSettings } from '../context/SettingsContext';

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const HOURS_12 = ['12', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11'];
const MINUTES = ['00', '15', '30', '45'];

const TimePicker = ({ value, onChange }) => {
  const [h, m] = value ? value.split(':').map(Number) : [9, 0];
  const isPM = h >= 12;
  const h12 = h % 12 === 0 ? 12 : h % 12;
  const minStr = String(m).padStart(2, '0');

  const emit = (newH12, newMin, newIsPM) => {
    let h24 = newH12 % 12;
    if (newIsPM) h24 += 12;
    onChange(`${String(h24).padStart(2, '0')}:${newMin}`);
  };

  const sel = 'border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white';
  return (
    <div className="flex gap-2">
      <select value={h12} onChange={e => emit(Number(e.target.value), minStr, isPM)} className={sel}>
        {HOURS_12.map(h => <option key={h} value={h}>{h}</option>)}
      </select>
      <select value={minStr} onChange={e => emit(h12, e.target.value, isPM)} className={sel}>
        {MINUTES.map(min => <option key={min} value={min}>:{min}</option>)}
      </select>
      <select value={isPM ? 'PM' : 'AM'} onChange={e => emit(h12, minStr, e.target.value === 'PM')} className={sel}>
        <option value="AM">AM</option>
        <option value="PM">PM</option>
      </select>
    </div>
  );
};

const FREQUENCY_LABELS = {
  once: 'Specific date & time (once)',
  daily: 'Daily',
  weekdays: 'Weekdays (Mon–Fri)',
  weekends: 'Weekends (Sat–Sun)',
  weekly: 'Weekly',
  monthly: 'Monthly',
};

const formatDate = (iso) => iso ? new Date(iso).toLocaleString() : '—';

const scheduleLabel = (r) => {
  const t = r.time || '';
  switch (r.frequency) {
    case 'once':     return `Once on ${formatDate(r.remind_at)}`;
    case 'daily':    return `Daily at ${t}`;
    case 'weekdays': return `Weekdays at ${t}`;
    case 'weekends': return `Weekends at ${t}`;
    case 'weekly':   return `Weekly on ${DAY_NAMES[r.day_of_week] || '?'} at ${t}`;
    case 'monthly':  return `Monthly on day ${r.day_of_month} at ${t}`;
    default:         return '';
  }
};

const toDatetimeLocal = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};


const EMPTY_FORM = {
  name: '',
  description: '',
  frequency: 'once',
  remind_at: '',
  time: '09:00',
  day_of_week: '1',
  day_of_month: '1',
  notify_email: false,
  email_address: '',
  notify_telegram: false,
  followup_delay: '',
  include_confirm_link: false,
};

// ---- Form Modal ----

const tomorrowAt9 = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T09:00`;
};

const ReminderFormModal = ({ initial, onSave, onClose, defaultEmail }) => {
  const [form, setForm] = useState(initial || { ...EMPTY_FORM, remind_at: tomorrowAt9(), email_address: defaultEmail || '' });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState([]);

  const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const validate = () => {
    const errs = [];
    if (!form.name.trim()) errs.push('Name is required');
    if (form.frequency === 'once') {
      if (!form.remind_at) errs.push('Date & time is required');
      else if (new Date(form.remind_at) <= new Date()) errs.push('Date must be in the future');
    } else {
      if (!form.time) errs.push('Time is required');
      if (form.frequency === 'weekly' && form.day_of_week === '') errs.push('Day of week is required');
      if (form.frequency === 'monthly' && !form.day_of_month) errs.push('Day of month is required');
    }
    if (!form.notify_email && !form.notify_telegram) errs.push('Select at least one notification channel');
    if (form.notify_email && !form.email_address.trim()) errs.push('Email address is required');
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (errs.length) { setErrors(errs); return; }
    setSaving(true);
    setErrors([]);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description.trim() || null,
        frequency: form.frequency,
        notify_email: form.notify_email,
        email_address: form.notify_email ? form.email_address.trim() : null,
        notify_telegram: form.notify_telegram,
        followup_delay: form.followup_delay ? parseInt(form.followup_delay) : null,
        include_confirm_link: form.include_confirm_link,
      };
      if (form.frequency === 'once') {
        payload.remind_at = new Date(form.remind_at).toISOString();
      } else {
        payload.time = form.time;
        if (form.frequency === 'weekly') payload.day_of_week = parseInt(form.day_of_week);
        if (form.frequency === 'monthly') payload.day_of_month = parseInt(form.day_of_month);
      }
      await onSave(payload);
    } catch (err) {
      const msg = err.response?.data?.details || err.response?.data?.error || 'Save failed';
      setErrors(Array.isArray(msg) ? msg : [msg]);
    } finally {
      setSaving(false);
    }
  };

  const inputCls = 'w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';
  const labelCls = 'block text-sm font-medium text-gray-700 mb-1';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">
            {initial ? 'Edit Reminder' : 'New Reminder'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">&times;</button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          {errors.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-700 space-y-1">
              {errors.map((e, i) => <div key={i}>{e}</div>)}
            </div>
          )}

          {/* Name */}
          <div>
            <label className={labelCls}>Name <span className="text-red-500">*</span></label>
            <input type="text" value={form.name} onChange={e => set('name', e.target.value)}
              className={inputCls} placeholder="e.g. Weekly report" maxLength={255} />
          </div>

          {/* Description */}
          <div>
            <label className={labelCls}>Description</label>
            <textarea value={form.description} onChange={e => set('description', e.target.value)}
              rows={2} className={inputCls + ' resize-vertical'}
              placeholder="Optional notes..." maxLength={5000} />
          </div>

          {/* Frequency */}
          <div>
            <label className={labelCls}>Schedule <span className="text-red-500">*</span></label>
            <select value={form.frequency} onChange={e => set('frequency', e.target.value)} className={inputCls}>
              {Object.entries(FREQUENCY_LABELS).map(([v, l]) => (
                <option key={v} value={v}>{l}</option>
              ))}
            </select>
          </div>

          {/* Once: date + time picker */}
          {form.frequency === 'once' && (
            <div className="space-y-2">
              <div>
                <label className={labelCls}>Date <span className="text-red-500">*</span></label>
                <input type="date"
                  value={form.remind_at ? form.remind_at.split('T')[0] : ''}
                  onClick={e => { try { e.target.showPicker(); } catch {} }}
                  onChange={e => {
                    const date = e.target.value;
                    const time = (form.remind_at && form.remind_at.split('T')[1]) || '09:00';
                    set('remind_at', date ? `${date}T${time}` : '');
                  }}
                  min={new Date().toISOString().split('T')[0]}
                  className={inputCls + ' cursor-pointer'} />
              </div>
              <div>
                <label className={labelCls}>Time <span className="text-red-500">*</span></label>
                <TimePicker
                  value={(form.remind_at && form.remind_at.split('T')[1]) || '09:00'}
                  onChange={v => {
                    const date = (form.remind_at && form.remind_at.split('T')[0]) || '';
                    set('remind_at', date ? `${date}T${v}` : '');
                  }}
                />
              </div>
            </div>
          )}

          {/* Recurring: time picker */}
          {form.frequency !== 'once' && (
            <div>
              <label className={labelCls}>Time <span className="text-red-500">*</span></label>
              <TimePicker value={form.time} onChange={v => set('time', v)} />
            </div>
          )}

          {/* Weekly: day of week */}
          {form.frequency === 'weekly' && (
            <div>
              <label className={labelCls}>Day of Week <span className="text-red-500">*</span></label>
              <select value={form.day_of_week} onChange={e => set('day_of_week', e.target.value)} className={inputCls}>
                {DAY_NAMES.map((d, i) => <option key={i} value={i}>{d}</option>)}
              </select>
            </div>
          )}

          {/* Monthly: day of month */}
          {form.frequency === 'monthly' && (
            <div>
              <label className={labelCls}>Day of Month <span className="text-red-500">*</span></label>
              <select value={form.day_of_month} onChange={e => set('day_of_month', e.target.value)} className={inputCls}>
                {Array.from({ length: 31 }, (_, i) => i + 1).map(d => (
                  <option key={d} value={d}>{d}{d >= 29 ? ' *' : ''}</option>
                ))}
              </select>
              {parseInt(form.day_of_month) >= 29 && (
                <p className="text-xs text-gray-400 mt-1">* Days 29–31 clamp to month end in shorter months.</p>
              )}
            </div>
          )}

          {/* Notification channels */}
          <div className="border-t pt-3">
            <label className={labelCls}>
              Notification Channel <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-2 mt-1">
              <button type="button"
                onClick={() => set('notify_telegram', !form.notify_telegram)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border transition ${
                  form.notify_telegram
                    ? 'bg-primary-600 border-primary-600 text-white'
                    : 'bg-white border-gray-300 text-gray-600 hover:border-gray-400'
                }`}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.562 8.248-2.04 9.613c-.15.678-.546.843-1.107.524l-3-2.21-1.447 1.394c-.16.16-.295.295-.605.295l.213-3.053 5.56-5.023c.242-.213-.054-.333-.373-.12l-6.871 4.326-2.962-.924c-.643-.204-.657-.643.136-.953l11.566-4.46c.537-.194 1.006.13.93.591z"/>
                </svg>
                Telegram
              </button>
              <button type="button"
                onClick={() => set('notify_email', !form.notify_email)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border transition ${
                  form.notify_email
                    ? 'bg-primary-600 border-primary-600 text-white'
                    : 'bg-white border-gray-300 text-gray-600 hover:border-gray-400'
                }`}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                  <polyline points="22,6 12,13 2,6"/>
                </svg>
                Email
              </button>
            </div>
            {form.notify_email && (
              <input type="email" value={form.email_address}
                onChange={e => set('email_address', e.target.value)}
                className={'mt-3 ' + inputCls} placeholder="recipient@example.com" />
            )}
          </div>

          {/* Follow-up */}
          <div>
            <label className={labelCls}>Follow-up reminder</label>
            <select value={form.followup_delay} onChange={e => set('followup_delay', e.target.value)} className={inputCls}>
              <option value="">None</option>
              <option value="30">30 minutes later</option>
              <option value="60">1 hour later</option>
            </select>
          </div>

          {/* Confirmation link */}
          <div>
            <button type="button"
              onClick={() => set('include_confirm_link', !form.include_confirm_link)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border transition ${
                form.include_confirm_link
                  ? 'bg-primary-600 border-primary-600 text-white'
                  : 'bg-white border-gray-300 text-gray-600 hover:border-gray-400'
              }`}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
              </svg>
              Confirmation link
            </button>
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t">
            <button type="button" onClick={onClose}
              className="px-4 py-2 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition">
              Cancel
            </button>
            <button type="submit" disabled={saving}
              className="px-4 py-2 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-60 transition">
              {saving ? 'Saving...' : (initial ? 'Update' : 'Create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ---- Status Badge ----

const StatusBadge = ({ r }) => {
  const confirmedBadge = r.latest_confirmed_at
    ? <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">&#10003; Confirmed</span>
    : null;

  if (r.frequency !== 'once') {
    return <>{<span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800">Recurring</span>}{confirmedBadge}</>;
  }
  if (!r.active) {
    return <>{<span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">Sent</span>}{confirmedBadge}</>;
  }
  if (new Date(r.next_remind_at) < new Date()) {
    return <>{<span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">Pending</span>}{confirmedBadge}</>;
  }
  return <>{<span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">Scheduled</span>}{confirmedBadge}</>;
};

// ---- Page ----

const Reminders = () => {
  const { settings } = useSettings();
  const [reminders, setReminders] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await remindersApi.getAll();
      setReminders(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load reminders');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const [confirmId, setConfirmId] = useState(null);

  const handleSave = async (values) => {
    if (editing) {
      await remindersApi.update(editing.id, values);
    } else {
      await remindersApi.create(values);
    }
    setShowForm(false);
    setEditing(null);
    await load();
  };

  const handleEdit = (r) => {
    setEditing({
      ...r,
      remind_at: toDatetimeLocal(r.remind_at),
      time: r.time || '09:00',
      day_of_week: r.day_of_week != null ? String(r.day_of_week) : '1',
      day_of_month: r.day_of_month != null ? String(r.day_of_month) : '1',
      notify_email: !!r.notify_email,
      notify_telegram: !!r.notify_telegram,
      email_address: r.email_address || '',
      followup_delay: r.followup_delay ? String(r.followup_delay) : '',
      include_confirm_link: !!r.include_confirm_link,
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    setConfirmId(null);
    try {
      await remindersApi.delete(id);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Delete failed');
    }
  };

  const channels = (r) => [r.notify_email && 'Email', r.notify_telegram && 'Telegram'].filter(Boolean).join(', ');

  return (
    <div className="min-h-screen bg-gray-50">
      {confirmId && (
        <ConfirmModal
          message="Delete this reminder?"
          onConfirm={() => handleDelete(confirmId)}
          onCancel={() => setConfirmId(null)}
        />
      )}
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">My Reminders</h1>
          </div>
          <button
            onClick={() => { setEditing(null); setShowForm(true); }}
            className="w-10 h-10 bg-primary-600 text-white rounded-full shadow-md hover:bg-primary-700 transition flex items-center justify-center"
            title="New Reminder"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
          </button>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-700">{error}</div>
        )}

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : reminders.length === 0 ? (
          <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
            <div className="text-4xl mb-3">&#9201;</div>
            <h3 className="text-lg font-medium text-gray-900 mb-1">No reminders yet</h3>
            <p className="text-sm text-gray-500">Create a reminder to get notified on your schedule.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {reminders.map(r => (
              <div key={r.id} className="bg-white rounded-lg border border-gray-200 px-5 py-4 flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-gray-900">{r.name}</span>
                    <StatusBadge r={r} />
                  </div>
                  {r.description && (
                    <p className="text-sm text-gray-500 mt-1 line-clamp-2">{r.description}</p>
                  )}
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs text-gray-400">
                    <span>&#128197; {scheduleLabel(r)}</span>
                    {r.frequency !== 'once' && r.active && r.next_remind_at && (
                      <span>&#128336; Next: {formatDate(r.next_remind_at)}</span>
                    )}
                    {r.last_sent_at && (
                      <span>&#10003; Last sent: {formatDate(r.last_sent_at)}</span>
                    )}
                    {r.latest_confirmed_at && (
                      <span>&#10003; Confirmed: {formatDate(r.latest_confirmed_at)}</span>
                    )}
                    <span>&#128276; {channels(r)}</span>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => handleEdit(r)}
                    className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-md transition"
                    title="Edit">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                    </svg>
                  </button>
                  <button onClick={() => setConfirmId(r.id)}
                    className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition"
                    title="Delete">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="3 6 5 6 21 6" />
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                      <line x1="10" y1="11" x2="10" y2="17" />
                      <line x1="14" y1="11" x2="14" y2="17" />
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showForm && (
        <ReminderFormModal
          initial={editing}
          onSave={handleSave}
          onClose={() => { setShowForm(false); setEditing(null); }}
          defaultEmail={settings.default_email}
        />
      )}
    </div>
  );
};

export default Reminders;
